import React, { useState, useEffect, useMemo } from 'react';
import { Globe, Mail, Phone, Linkedin, User, Instagram } from 'lucide-react';
import AnimatedCounter from '../components/AnimatedCounter';
import { useSiteTranslation } from '../hooks/useSiteTranslation';
import axios from 'axios';
import { getAssetUrl } from '../utils/assetUrl';
import RichTextContent from '../components/RichTextContent';
import {
  getEquipeCargo,
  getEquipeDescricao,
  getEquipeNome,
  type EquipeI18nBlock,
  type ResolvedSectionContent
} from '../i18n/localizeContent';
import { useLandingSections } from '../hooks/useLandingSections';
import EditableSection from '../components/Cms/EditableSection';
import { resolveIcon } from '../utils/iconCatalog';

interface MembroEquipe {
  _id: string;
  nome: string;
  cargo: string;
  descricao?: string;
  i18n?: EquipeI18nBlock;
  foto?: string;
  email?: string;
  telefone?: string;
  linkedin?: string;
  instagram?: string;
  especialidades?: string[];
  formacao?: Array<{
    titulo: string;
    instituicao: string;
    ano: number;
  }>;
  experiencia?: Array<{
    cargo: string;
    empresa: string;
    periodo: string;
    descricao: string;
  }>;
  idiomas?: Array<{
    idioma: string;
    nivel: string;
  }>;
}

const About: React.FC = () => {
  const [team, setTeam] = useState<MembroEquipe[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [showTeamSection, setShowTeamSection] = useState(true);

  const { ts, t, i18n } = useSiteTranslation();
  const { sections, refetch } = useLandingSections('about');

  const heroFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: ts('about.title'), title2: '', subtitle: t('about.subtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: []
    }),
    [ts, t]
  );

  const missionFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('about.missionTitle'), title2: '', subtitle: '',
      paragraph1: t('about.missionP1'), paragraph2: t('about.missionP2'),
      visionTitle: t('about.visionTitle'), visionText: t('about.visionText'),
      ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: []
    }),
    [t]
  );

  const valuesFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('about.valuesTitle'), title2: '', subtitle: t('about.valuesSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [
        { id: '0', icon: 'Heart', title: t('about.passion'), description: t('about.passionDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '1', icon: 'Target', title: t('about.focus'), description: t('about.focusDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '2', icon: 'Lightbulb', title: t('about.innovation'), description: t('about.innovationDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '3', icon: 'Users', title: t('about.collaboration'), description: t('about.collaborationDesc'), label: '', duration: '', features: [], popularVisas: [], requirements: [] }
      ]
    }),
    [t]
  );

  const numbersFallback: ResolvedSectionContent = useMemo(
    () => ({
      title: t('about.numbersTitle'), title2: '', subtitle: t('about.numbersSubtitle'),
      paragraph1: '', paragraph2: '', visionTitle: '', visionText: '', ctaPrimaryLabel: '', ctaSecondaryLabel: '',
      items: [
        { id: '0', number: 500, numberSuffix: '+', title: '', description: t('about.achievements.families.description'), label: t('about.achievements.families.label'), duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '1', number: 15, numberSuffix: '+', title: '', description: t('about.achievements.countries.description'), label: t('about.achievements.countries.label'), duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '2', number: 95, numberSuffix: '%', title: '', description: t('about.achievements.success.description'), label: t('about.achievements.success.label'), duration: '', features: [], popularVisas: [], requirements: [] },
        { id: '3', number: 10, numberSuffix: '+', title: '', description: t('about.achievements.experience.description'), label: t('about.achievements.experience.label'), duration: '', features: [], popularVisas: [], requirements: [] }
      ]
    }),
    [t]
  );

  useEffect(() => {
    const fetchTeamAndSettings = async () => {
      try {
        setLoadingTeam(true);

        const settingsResponse = await axios.get('/settings');
        const teamSettings = settingsResponse.data.team;

        if (teamSettings && !teamSettings.showTeamSection) {
          setShowTeamSection(false);
          setLoadingTeam(false);
          return;
        }

        const teamResponse = await axios.get('/equipe');
        const teamData = teamResponse.data;

        if (teamSettings && teamSettings.hideWhenEmpty && teamData.length === 0) {
          setShowTeamSection(false);
        } else {
          setTeam(teamData);
        }
      } catch (error) {
        console.error('Erro ao buscar equipe:', error);
        setTeam([
          {
            _id: '1',
            nome: 'Dr. Ana Silva',
            cargo: 'Diretora Executiva',
            descricao: 'Especialista em Direito Internacional com mais de 15 anos de experiência.',
            especialidades: ['Direito Internacional', 'Processos de Imigração'],
            email: 'ana@neia.com',
            telefone: '(11) 99999-9999',
            linkedin: 'https://linkedin.com/in/ana-silva',
          },
          {
            _id: '2',
            nome: 'Carlos Mendes',
            cargo: 'Consultor Sênior',
            descricao: 'Especialista em vistos de trabalho e processos administrativos.',
            especialidades: ['Vistos de Trabalho', 'Processos Administrativos'],
            email: 'carlos@neia.com',
            telefone: '(11) 99999-9998',
            linkedin: 'https://linkedin.com/in/carlos-mendes',
          },
        ]);
      } finally {
        setLoadingTeam(false);
      }
    };

    fetchTeamAndSettings();
  }, []);

  return (
    <div className="min-h-screen">
      <section className="hero-gradient section-padding">
        <div className="container-custom">
          <EditableSection sectionKey="about.hero" data={sections['about.hero']} fallback={heroFallback} onSaved={refetch}>
            {(content) => (
              <div className="max-w-4xl mx-auto text-center px-4">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 md:mb-6">
                  {content.title}
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 mb-6 md:mb-8 leading-relaxed">
                  {content.subtitle}
                </p>
              </div>
            )}
          </EditableSection>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom px-4">
          <EditableSection sectionKey="about.mission" data={sections['about.mission']} fallback={missionFallback} onSaved={refetch}>
            {(content) => (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                    {content.title}
                  </h2>
                  <p className="text-base sm:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                    {content.paragraph1}
                  </p>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    {content.paragraph2}
                  </p>
                </div>
                <div className="relative">
                  <div className="bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl p-8">
                    <div className="text-center">
                      <Globe className="w-16 h-16 text-primary-600 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{content.visionTitle}</h3>
                      <p className="text-gray-600">{content.visionText}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </EditableSection>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom px-4">
          <EditableSection sectionKey="about.values" data={sections['about.values']} fallback={valuesFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-12 md:mb-16">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                    {content.title}
                  </h2>
                  <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                    {content.subtitle}
                  </p>
                </div>

                <div className="grid-centered sm:[--cols:2] lg:[--cols:4] [--gap:1.5rem] md:[--gap:2rem]">
                  {content.items.map((item) => {
                    const Icon = resolveIcon(item.icon);
                    return (
                      <div key={item.id} className="card text-center group hover:-translate-y-2 transition-transform duration-300">
                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-600 transition-colors duration-300">
                          <Icon className="w-8 h-8 text-primary-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      {showTeamSection && !loadingTeam && team.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-custom px-4">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                {t('about.teamTitle')}
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                {t('about.teamSubtitle')}
              </p>
            </div>

            <div
              className={
                team.length === 1
                  ? 'flex justify-center'
                  : 'grid-centered sm:[--cols:2] lg:[--cols:3] xl:[--cols:4] [--gap:1.5rem] md:[--gap:2rem]'
              }
            >
              {team.map((member) => {
                const solo = team.length === 1;
                const nome = getEquipeNome(member, i18n.language);
                const cargo = getEquipeCargo(member, i18n.language);
                const descricao = getEquipeDescricao(member, i18n.language);

                const photo = (
                  <div
                    className={`bg-gray-200 rounded-full overflow-hidden flex-shrink-0 ${
                      solo ? 'w-32 h-32 sm:w-40 sm:h-40 md:w-44 md:h-44' : 'w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4'
                    }`}
                  >
                    {member.foto ? (
                      <img
                        src={getAssetUrl(member.foto)}
                        alt={member.nome}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User
                          className={
                            solo ? 'w-14 h-14 sm:w-16 sm:h-16 text-gray-400' : 'w-6 h-6 sm:w-8 sm:h-8 text-gray-400'
                          }
                        />
                      </div>
                    )}
                  </div>
                );

                const details = (
                  <>
                    <h3
                      className={`font-semibold text-gray-900 mb-1 sm:mb-2 ${
                        solo ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'
                      }`}
                    >
                      {nome}
                    </h3>
                    <p
                      className={`text-primary-600 font-medium mb-2 ${
                        solo ? 'text-lg sm:text-xl' : 'text-sm sm:text-base'
                      }`}
                    >
                      {cargo}
                    </p>

                    {descricao && (
                      <RichTextContent
                        html={descricao}
                        className={`text-sm text-gray-600 mb-3 ${solo ? 'sm:text-base' : ''}`}
                      />
                    )}

                    {member.especialidades && member.especialidades.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1">{t('about.specialties')}</p>
                        <div
                          className={`flex flex-wrap gap-1 ${solo ? 'justify-center sm:justify-start' : 'justify-center'}`}
                        >
                          {member.especialidades.slice(0, 2).map((especialidade, index) => (
                            <span
                              key={index}
                              className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full"
                            >
                              {especialidade}
                            </span>
                          ))}
                          {member.especialidades.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{member.especialidades.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div
                      className={`flex space-x-3 ${solo ? 'justify-center sm:justify-start' : 'justify-center'}`}
                    >
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="text-primary-600 hover:text-primary-800 transition-colors"
                          title={t('about.sendEmail')}
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      )}
                      {member.telefone && (
                        <a
                          href={`tel:${member.telefone}`}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title={t('about.call')}
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {member.instagram && (
                        <a
                          href={member.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-600 hover:text-pink-800 transition-colors"
                          title="Instagram"
                        >
                          <Instagram className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </>
                );

                if (solo) {
                  return (
                    <div
                      key={member._id}
                      className="card w-full max-w-3xl lg:max-w-4xl px-6 py-8 sm:px-8 group transition-transform duration-300 hover:-translate-y-1"
                    >
                      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 text-center sm:text-left">
                        {photo}
                        <div className="flex-1 min-w-0">{details}</div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={member._id}
                    className="card text-center group transition-transform duration-300 hover:-translate-y-1 md:hover:-translate-y-2"
                  >
                    {photo}
                    {details}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="section-padding bg-gray-50">
        <div className="container-custom px-4">
          <EditableSection sectionKey="about.numbers" data={sections['about.numbers']} fallback={numbersFallback} onSaved={refetch}>
            {(content) => (
              <>
                <div className="text-center mb-12 md:mb-16">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                    {content.title}
                  </h2>
                  <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                    {content.subtitle}
                  </p>
                </div>

                <div className="grid-centered [--cols:2] lg:[--cols:4] [--gap:1.5rem] md:[--gap:2rem]">
                  {content.items.map((item) => (
                    <div key={item.id} className="card text-center">
                      <div className="text-4xl font-bold text-primary-600 mb-2">
                        <AnimatedCounter
                          value={item.number || 0}
                          suffix={item.numberSuffix || ''}
                          className="text-4xl font-bold text-primary-600"
                        />
                      </div>
                      <div className="text-lg font-semibold text-gray-900 mb-2">{item.label}</div>
                      <div className="text-sm text-gray-600">{item.description}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </EditableSection>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom text-center px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6">
            {t('about.ctaTitle')}
          </h2>
          <p className="text-lg sm:text-xl mb-6 md:mb-8 opacity-90 max-w-2xl mx-auto">
            {t('about.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <a
              href="/contato"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-6 sm:px-8 rounded-lg transition-colors duration-200 text-center"
            >
              {t('about.ctaExpert')}
            </a>
            <a
              href="/imigracao"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-6 sm:px-8 rounded-lg transition-all duration-200 text-center"
            >
              {t('about.ctaServices')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
